"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.concentrationLimitPointerMethods = void 0;
const common_1 = require("@finley-technologies/common");
/**
 * This pointer method aims to help monitor exposure between the two LPs: "importer" and "exporter",
 * particularly that "importer" is not greater than a certain amount comparable to "exporter".
 *
 * For simplicity in comments, will refer to "eligibleExposureLessConcentrationLimitKickouts" by "eligibleLessKickouts"
 *
 * At the core level, the equation we're trying to manage is:
 * const importerBorrowingBaseAmount = eligibleLessKickouts * advanceRate
 * const exporterBorrowingBaseAmount = eligibleLessKickouts * advanceRate
 * ( importerBorrowingBaseAmount / ( importerBorrowingBaseAmount + exporterBorrowingBaseAmount ) ) < limit
 *
 * So trying to reduce "importer's" eligibleLessKickouts until it's low enough in relation to "exporter's" eligibleLessKickouts.
 * If kickouts are needed, we're just going to pro-rate the needed kickouts across all receivables based on "eligibleLessKickouts"
 */
const importerExposureContributionPointer = (params) => {
    const { concentrationReceivables, valueConfig } = params;
    // *** Set up hardcoded values ***
    const intermediateFieldAdvanceRateKey = 'advanceRateToUse';
    const envLoanProductSlugToIdMap = {
        development: {
            importerLoanProduct: '3eb3703e-1374-463e-9c56-8d670263b84f',
            exporterLoanProduct: 'f4f287cd-e4a8-409b-88f9-bd127a097499',
        },
        stage: {
            importerLoanProduct: '3eb3703e-1374-463e-9c56-8d670263b84f',
            exporterLoanProduct: 'f4f287cd-e4a8-409b-88f9-bd127a097499',
        },
        prod: {
            importerLoanProduct: 'ee9027d4-f82d-4476-9121-4cbf5410bcb2',
            exporterLoanProduct: 'a622761a-0861-407b-ac37-33252b896f72',
        },
    };
    const env = common_1.utils.EnvUtils.getEnv();
    const lpSlugToIdMap = envLoanProductSlugToIdMap[env];
    // *** Set up recording variables ***
    let importerAdvanceRate = null; // Used later to back into target "importer" target "eligibleLessKickouts"
    let importerEligibleExposureLessConcentrationKickouts = 0; // Used to compare against for potential overage and kickout pro-rata
    let importerReceivableBorrowingBaseAmount = 0; // Used to calculate concentration and potential overage
    let exporterReceivableBorrowingBaseAmount = 0; // Used to calculate concentration and potential overage
    let totalEligibleExposureLessConcentrationLimitKickouts = 0; // Purely for concentrationAmount
    // In case there are kickouts, just need to only loop through the "importer" (target) receivables
    // so storing them upfront here for simplicity later
    const importerReceivables = [];
    // *** Manage calculations by looping through each receivable ***
    for (const receivable of concentrationReceivables) {
        const { finleyReceivableId, loanProductId, dynamicIntermediateFields, concentrationLimitFields, } = receivable;
        const { eligibleExposureLessConcentrationLimitKickouts, } = concentrationLimitFields;
        const advanceRate = dynamicIntermediateFields[intermediateFieldAdvanceRateKey];
        (0, common_1.finleyAssert)(typeof advanceRate === 'number', `mundiTradeSpv1Svb1.importerExposureContribution: Incurred unsupported advance rate [key:${intermediateFieldAdvanceRateKey}] [receivableId:${finleyReceivableId}]`);
        if (loanProductId === lpSlugToIdMap.importerLoanProduct) {
            // Managing "importer" loan product
            importerReceivables.push(receivable);
            importerEligibleExposureLessConcentrationKickouts += eligibleExposureLessConcentrationLimitKickouts;
            importerReceivableBorrowingBaseAmount +=
                eligibleExposureLessConcentrationLimitKickouts * advanceRate;
            if (importerAdvanceRate === null) {
                importerAdvanceRate = advanceRate;
            }
        }
        else if (loanProductId === lpSlugToIdMap.exporterLoanProduct) {
            // Managing "exporter" loan product
            exporterReceivableBorrowingBaseAmount +=
                eligibleExposureLessConcentrationLimitKickouts * advanceRate;
        }
        else {
            throw new common_1.error.FinleyError(`mundiTradeSpv1Svb1.importerExposureContribution: Incurred unsupported [loanProductId:${loanProductId}]`);
        }
        totalEligibleExposureLessConcentrationLimitKickouts += eligibleExposureLessConcentrationLimitKickouts;
    }
    // Didn't come across any "importer" LP receivables, so can just return early since no kickouts needed
    if (importerReceivables.length === 0) {
        return {
            kickouts: {},
            calculations: {
                concentrationLimit: valueConfig.limit,
                concentrationAmount: totalEligibleExposureLessConcentrationLimitKickouts,
                concentration: 0,
            },
        };
    }
    (0, common_1.finleyAssert)(importerAdvanceRate !== null, `mundiTradeSpv1Svb1.importerExposureContribution: Failed to properly set "importer" advance rate`);
    // *** Manage kickout calculations ***
    // Using the limit, can back in to the max total amount between "importer" and "exporter"
    const maxTotalReceivableBorrowingAmount = exporterReceivableBorrowingBaseAmount / (1 - valueConfig.limit);
    // Take away "exporter" amount to infer what the max amount "importer" can have
    const maxImporterReceivableBorrowingAmount = maxTotalReceivableBorrowingAmount - exporterReceivableBorrowingBaseAmount;
    // Backtrack advance rate impact to derive max "eligibleLessKickout" amount for "importer"
    const maxImporterEligibleExposureLessConcentrationKickouts = common_1.utils.divideByOrZero(maxImporterReceivableBorrowingAmount, importerAdvanceRate);
    // Compare current "importer" "eligiblyeLessKickout" compared to max amount to derive kickouts
    const importerEligibleLessKickoutReduction = Math.max(importerEligibleExposureLessConcentrationKickouts -
        maxImporterEligibleExposureLessConcentrationKickouts, 0);
    const kickouts = {};
    if (importerEligibleLessKickoutReduction > 0) {
        for (const receivable of importerReceivables) {
            const { finleyReceivableId, concentrationLimitFields } = receivable;
            const { eligibleExposureLessConcentrationLimitKickouts, } = concentrationLimitFields;
            // Recall that "importerEligibleExposureLessConcentrationKickouts" is the total
            // "eligibleExposureLessConcentrationLimitKickouts" across all "importer" receivables.
            // So these percents should ultimately add up to 100% and manage kickout proration properly
            const proRatePercent = eligibleExposureLessConcentrationLimitKickouts /
                importerEligibleExposureLessConcentrationKickouts;
            const proRateReductionAmount = proRatePercent * importerEligibleLessKickoutReduction;
            kickouts[finleyReceivableId] = proRateReductionAmount;
        }
    }
    // *** Manage concentration calculation ***
    const concentration = importerReceivableBorrowingBaseAmount /
        (importerReceivableBorrowingBaseAmount +
            exporterReceivableBorrowingBaseAmount);
    return {
        kickouts,
        calculations: {
            concentrationLimit: valueConfig.limit,
            concentrationAmount: totalEligibleExposureLessConcentrationLimitKickouts,
            concentration,
        },
    };
};
/**
 * Pointer method to help compare "non AADC excess contribution" against "eligibleExposure" with the help of "AADC flag".
 * We only care about receivables that have "false" for the "AADC flag".
 * To note, this will only be for the "exporter" loan product
 */
const exporterNonAadcContributionPointer = (params) => {
    const { concentrationReceivables, valueConfig, totalConcentrationExposure, } = params;
    // Just return early if no receivables are passed in since below assumes there are receivables
    // available so just managing that edge case
    if (concentrationReceivables.length === 0) {
        return {
            kickouts: {},
            calculations: {
                concentrationLimit: valueConfig.limit,
                concentrationAmount: totalConcentrationExposure,
                concentration: 0,
            },
        };
    }
    // *** Set up hardcoded values ***
    const intermediateFieldAadcFlagKey = 'aadcFlag';
    const baseFieldInsuranceCoverageKey = 'insurance_coverage';
    const baseFieldTotalMundiBalanceKey = 'total_mundi_balance_current';
    const intermediateFieldExclusionPolicyLimitKey = 'exclusionPolicyLimit';
    // *** Set up helper function to check & retrieve required values ***
    const extractReceivableAadcFields = (receivable) => {
        const { finleyReceivableId, dynamicIntermediateFields } = receivable;
        const aadcFlag = dynamicIntermediateFields[intermediateFieldAadcFlagKey];
        (0, common_1.finleyAssert)(typeof aadcFlag === 'boolean', `mundiTradeSpv1Svb1.exporterNonAadcContributionPointer: incurred non-boolean AADC flag intermediate field [type:${typeof aadcFlag}] [receivableId:${finleyReceivableId}] [key:${intermediateFieldAadcFlagKey}]`);
        return { aadcFlag };
    };
    // *** Set up recording variables ***
    let totalNotAadcFlagEligibleExposure = 0;
    let totalEligibleExposure = 0;
    // In case there are kickouts, just need to only loop through the "importer" (target) receivables
    // so storing them upfront here for simplicity later
    const eligibleFalseAadcReceivables = [];
    // *** Manage calculations by looping through each receivable ***
    for (const receivable of concentrationReceivables) {
        const { eligibilityFields } = receivable;
        const { eligibleExposure, isEligible } = eligibilityFields;
        const { aadcFlag } = extractReceivableAadcFields(receivable);
        if (isEligible && aadcFlag === false) {
            totalNotAadcFlagEligibleExposure += eligibleExposure;
            eligibleFalseAadcReceivables.push(receivable);
        }
        totalEligibleExposure += eligibleExposure;
    }
    // *** Manage kickout calculations ***
    const maxNonAadcExcessContribution = totalEligibleExposure * valueConfig.limit;
    const nonAadcContributionKickoutReductionPreExclusion = Math.max(totalNotAadcFlagEligibleExposure - maxNonAadcExcessContribution, 0);
    let totalCappedInsuranceBalance = 0;
    for (const receivable of eligibleFalseAadcReceivables) {
        const { finleyReceivableId, baseFields, eligibilityFields } = receivable;
        const { eligibleExposure } = eligibilityFields;
        const insuranceCoverage = baseFields[baseFieldInsuranceCoverageKey];
        (0, common_1.finleyAssert)(typeof insuranceCoverage === 'number', `mundiTradeSpv1Svb1.exporterNonAadcContributionPointer: incurred non-number insurance coverage base field [type:${typeof insuranceCoverage}] [receivableId:${finleyReceivableId}] [key:${baseFieldInsuranceCoverageKey}]`);
        const balance = baseFields[baseFieldTotalMundiBalanceKey];
        (0, common_1.finleyAssert)(typeof balance === 'number', `mundiTradeSpv1Svb1.exporterNonAadcContributionPointer: incurred non-number total mundi balance base field [type:${typeof balance}] [receivableId:${finleyReceivableId}] [key:${baseFieldTotalMundiBalanceKey}]`);
        // Per Wingate, we're trying to calculate the following:
        // MIN(IFERROR(insurance_coverage / total_mundi_balance_current, 0), 1) * eligibleExposure
        const insuredBalancePercent = common_1.utils.divideByOrZero(insuranceCoverage, balance);
        const cappedInsuredBalancePercent = Math.min(insuredBalancePercent, 1);
        const cappedInsuranceBalance = cappedInsuredBalancePercent * eligibleExposure;
        totalCappedInsuranceBalance += cappedInsuranceBalance;
    }
    // "exclusionPolicy" will be a hardcoded static intermediate value so just using the first receviable
    // since it'll be the same across all receivables
    const { dynamicIntermediateFields } = concentrationReceivables[0];
    const exclusionPolicyLimit = dynamicIntermediateFields[intermediateFieldExclusionPolicyLimitKey];
    (0, common_1.finleyAssert)(typeof exclusionPolicyLimit === 'number', `mundiTradeSpv1Svb1.exporterNonAadcContributionPointer: incurred non-number exclusion policy intermediate field [type:${typeof exclusionPolicyLimit}] [key:${intermediateFieldExclusionPolicyLimitKey}]`);
    const exclusionReductionToCarveOut = Math.min(totalCappedInsuranceBalance, exclusionPolicyLimit);
    const nonAadcContributionKickoutReduction = Math.max(nonAadcContributionKickoutReductionPreExclusion -
        exclusionReductionToCarveOut, 0);
    const kickouts = {};
    if (nonAadcContributionKickoutReduction > 0) {
        for (const receivable of eligibleFalseAadcReceivables) {
            const { finleyReceivableId, eligibilityFields: { eligibleExposure }, } = receivable;
            const { aadcFlag } = extractReceivableAadcFields(receivable);
            // This is technically excessive since we're only looping through "false"
            // AADC receivables so this is just an additional sanity check
            if (aadcFlag === true) {
                continue;
            }
            const proRatePercent = common_1.utils.divideByOrZero(eligibleExposure, totalNotAadcFlagEligibleExposure);
            const proRateReductionAmount = proRatePercent * nonAadcContributionKickoutReduction;
            kickouts[finleyReceivableId] = proRateReductionAmount;
        }
    }
    // *** Manage concentration calculation ***
    const concentrationAmount = totalNotAadcFlagEligibleExposure;
    const concentration = common_1.utils.divideByOrZero(concentrationAmount, totalEligibleExposure);
    return {
        kickouts,
        calculations: {
            concentrationLimit: valueConfig.limit,
            concentrationAmount,
            concentration,
        },
    };
};
exports.concentrationLimitPointerMethods = [
    {
        method: 'mundiTradeSpv1Svb1.importerExposureContribution',
        pointerImplementation: importerExposureContributionPointer,
    },
    {
        method: 'mundiTradeSpv1Svb1.exporterNonAadcContribution',
        pointerImplementation: exporterNonAadcContributionPointer,
    },
];
//# sourceMappingURL=concentrationLimitPointerMethods.js.map